import { supabase } from "@/lib/supabase";

export type MovementType = "entrada" | "salida" | "ajuste" | "inicial" | "correccion" | "transferencia";

interface MovementResult {
  ok: boolean;
  error: string | null;
  newStock: number;
}

async function readStock(productId: string, branchId: string): Promise<number> {
  const { data } = await supabase
    .from("inventory")
    .select("stock")
    .eq("product_id", productId)
    .eq("branch_id", branchId)
    .maybeSingle();
  return Number(data?.stock ?? 0);
}

async function writeStock(
  productId: string,
  branchId: string,
  newStock: number,
  unit: string | null,
): Promise<{ error: string | null }> {
  const { error } = await supabase.from("inventory").upsert(
    {
      product_id: productId,
      branch_id: branchId,
      stock: newStock,
      unit: unit ?? undefined,
      updated_at: new Date().toISOString(),
    },
    { onConflict: "product_id,branch_id" },
  );
  return { error: error ? "No se pudo actualizar el stock." : null };
}

interface RegisterMovementParams {
  productId: string;
  branchId: string;
  movementType: MovementType;
  /** For entrada/salida: the positive amount. For ajuste: the target absolute stock. */
  quantity: number;
  unit?: string | null;
  reason?: string | null;
  reference?: string | null;
  notes?: string | null;
  userId: string | null;
}

/**
 * Registers an auditable stock movement and updates the inventory row.
 * The stock is NEVER modified without a movement record.
 */
export async function registerMovement(params: RegisterMovementParams): Promise<MovementResult> {
  const { productId, branchId, movementType, quantity, unit, reason, reference, notes, userId } = params;
  const current = await readStock(productId, branchId);

  let delta: number;
  if (movementType === "salida") {
    delta = -Math.abs(quantity);
  } else if (movementType === "ajuste") {
    delta = quantity - current;
  } else {
    delta = Math.abs(quantity);
  }

  const newStock = current + delta;
  if (newStock < 0) {
    return { ok: false, error: "El stock no puede quedar en negativo.", newStock: current };
  }

  const stockResult = await writeStock(productId, branchId, newStock, unit ?? null);
  if (stockResult.error) {
    return { ok: false, error: stockResult.error, newStock: current };
  }

  const { error: movementError } = await supabase.from("inventory_movements").insert({
    product_id: productId,
    branch_id: branchId,
    movement_type: movementType,
    quantity: delta,
    reference: reference ?? null,
    reason: reason ?? null,
    notes: notes ?? null,
    user_id: userId,
  });

  if (movementError) {
    return { ok: false, error: "El stock cambió pero el movimiento no se pudo registrar.", newStock };
  }

  return { ok: true, error: null, newStock };
}

interface TransferParams {
  productId: string;
  fromBranchId: string;
  toBranchId: string;
  quantity: number;
  unit?: string | null;
  reason?: string | null;
  userId: string | null;
}

/** Transfers stock between branches, recording an out movement and an in movement. */
export async function transferStock(params: TransferParams): Promise<{ ok: boolean; error: string | null }> {
  const { productId, fromBranchId, toBranchId, quantity, unit, reason, userId } = params;

  if (fromBranchId === toBranchId) {
    return { ok: false, error: "Las sucursales de origen y destino deben ser diferentes." };
  }
  const qty = Math.abs(quantity);
  if (qty <= 0) {
    return { ok: false, error: "La cantidad debe ser mayor que cero." };
  }

  const available = await readStock(productId, fromBranchId);
  if (available < qty) {
    return { ok: false, error: `Stock insuficiente en la sucursal de origen (disponible: ${available}).` };
  }

  const reference = `TRF-${Date.now()}`;

  const outResult = await registerMovement({
    productId,
    branchId: fromBranchId,
    movementType: "salida",
    quantity: qty,
    unit: unit ?? null,
    reason: reason ?? "Transferencia entre sucursales",
    reference,
    notes: "Salida por transferencia",
    userId,
  });
  if (!outResult.ok) return { ok: false, error: outResult.error };

  const inResult = await registerMovement({
    productId,
    branchId: toBranchId,
    movementType: "entrada",
    quantity: qty,
    unit: unit ?? null,
    reason: reason ?? "Transferencia entre sucursales",
    reference,
    notes: "Entrada por transferencia",
    userId,
  });
  if (!inResult.ok) return { ok: false, error: inResult.error };

  return { ok: true, error: null };
}